import { useMemo } from "react";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { nameComparator, userDisplayNameComparator } from "@mapmycustomers/shared/util/comparator";

import SortOrder from "@app/scene/dashboard/components/Sidebar/enum/SortOrder";
import isNegativeMetric from "@app/scene/dashboard/components/Sidebar/utils/isNegativeMetric";
import PerformanceData, { ScopeKey } from "@app/scene/dashboard/types/PerformanceData";

const compareValues = (a: number | undefined, b: number | undefined, multiplier: number) => {
  if (a === undefined || b === undefined) {
    if (a === b) {
      return 0;
    }
    return multiplier * (a === undefined ? 1 : -1);
  }

  return multiplier * (b - a);
};

export interface Sorters {
  teamSorter: <T extends Pick<Team, "id" | "name">>(teams: T[]) => T[];
  userSorter: <T extends Pick<User, "fullName" | "id" | "username">>(
    users: T[],
    teamId?: "noTeam" | Team["id"]
  ) => T[];
}

const useSorters = (
  sort: SortOrder,
  rankingMetric: IField,
  performanceData: PerformanceData
): Sorters =>
  useMemo((): Sorters => {
    if (sort === SortOrder.NAME) {
      return {
        teamSorter: <T extends Pick<Team, "id" | "name">>(teams: T[]) =>
          [...teams].sort(nameComparator),
        userSorter: <T extends Pick<User, "fullName" | "id" | "username">>(users: T[]) =>
          [...users].sort(userDisplayNameComparator),
      };
    }

    const multiplier = isNegativeMetric(rankingMetric.name as LeaderboardMetricFieldName) ? -1 : 1;

    return {
      teamSorter: <T extends Pick<Team, "id" | "name">>(teams: T[]) =>
        [...teams].sort((a, b) => {
          const valueA = performanceData[`${a.id}_undefined`]?.value;
          const valueB = performanceData[`${b.id}_undefined`]?.value;
          return compareValues(valueA, valueB, multiplier) || nameComparator(a, b);
        }),
      userSorter: <T extends Pick<User, "fullName" | "id" | "username">>(
        users: T[],
        teamId?: "noTeam" | Team["id"]
      ) =>
        [...users].sort((a, b) => {
          const valueA = performanceData[`${teamId ?? "undefined"}_${a.id}` as ScopeKey]?.value;
          const valueB = performanceData[`${teamId ?? "undefined"}_${b.id}` as ScopeKey]?.value;
          return compareValues(valueA, valueB, multiplier) || userDisplayNameComparator(a, b);
        }),
    };
  }, [sort, rankingMetric, performanceData]);

export default useSorters;
